import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Button,
  Checkbox,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
} from "@mui/material";
import { fetchKycSubmissions, approveKyc, rejectKyc } from "../utils/api";
import { motion } from "framer-motion";

export default function KycPage() {
  const [kycList, setKycList] = useState([]);
  const [selected, setSelected] = useState([]);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [loadingIds, setLoadingIds] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const loadKyc = async () => {
    try {
      const res = await fetchKycSubmissions();
      setKycList(Array.isArray(res.data) ? res.data : res.data.data ?? []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadKyc();
  }, []);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAction = async (id, action) => {
    setLoadingIds((prev) => [...prev, id]);
    try {
      if (action === "approve") {
        await approveKyc(id);
        showSnackbar("KYC approved successfully", "success");
      } else {
        await rejectKyc(id, "Invalid details");
        showSnackbar("KYC rejected", "error");
      }
      loadKyc();
    } catch (err) {
      console.error(err);
      showSnackbar("Action failed", "error");
    } finally {
      setLoadingIds((prev) => prev.filter((x) => x !== id));
    }
  };

  const handleBulkAction = async (action) => {
    setLoadingIds(selected);
    try {
      await Promise.all(
        selected.map((id) =>
          action === "approve" ? approveKyc(id) : rejectKyc(id, "Invalid details")
        )
      );
      showSnackbar(`Bulk ${action} successful`, "success");
      setSelected([]);
      loadKyc();
    } catch (err) {
      console.error(err);
      showSnackbar("Bulk action failed", "error");
    } finally {
      setLoadingIds([]);
    }
  };

  const handleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const statusColor = (status) => {
    if (status === "approved") return "success";
    if (status === "rejected") return "error";
    return "warning";
  };

  return (
    <Box>
      {/* Header with bulk actions */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: "bold", color: "#0ff" }}>
          KYC Management
        </Typography>
        {selected.length > 0 && (
          <Box>
            <Button
              variant="contained"
              color="success"
              onClick={() => handleBulkAction("approve")}
              sx={{ mr: 1 }}
              disabled={loadingIds.length > 0}
            >
              {loadingIds.length > 0 ? <CircularProgress size={20} color="inherit" /> : "Approve Selected"}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleBulkAction("reject")}
              disabled={loadingIds.length > 0}
            >
              {loadingIds.length > 0 ? <CircularProgress size={20} color="inherit" /> : "Reject Selected"}
            </Button>
          </Box>
        )}
      </Box>

      {/* Table */}
      <Paper
        sx={{
          bgcolor: "rgba(25,25,50,0.7)",
          borderRadius: 3,
          backdropFilter: "blur(12px)",
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table sx={{ minWidth: 650 }} size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selected.length > 0 && selected.length === kycList.length}
                    onChange={(e) => {
                      if (e.target.checked) setSelected(kycList.map((k) => k.id));
                      else setSelected([]);
                    }}
                    sx={{ color: "#0ff" }}
                  />
                </TableCell>
                <TableCell>User</TableCell>
                <TableCell>Document Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {kycList.map((k, index) => {
                const isLoading = loadingIds.includes(k.id);
                return (
                  <motion.tr
                    key={k.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.includes(k.id)}
                        onChange={() => handleSelect(k.id)}
                        sx={{ color: "#0ff" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar
                          src={k.user?.avatar_url || ""}
                          sx={{ width: 32, height: 32 }}
                        >
                          {!k.user?.avatar_url && getInitials(k.user?.name)}
                        </Avatar>
                        {k.user?.name}
                      </Box>
                    </TableCell>
                    <TableCell>{k.document_type}</TableCell>
                    <TableCell>
                      <Chip
                        label={k.status}
                        color={statusColor(k.status)}
                        sx={{
                          fontWeight: "bold",
                          textTransform: "capitalize",
                          boxShadow: `0 0 6px ${
                            k.status === "approved" ? "green" : k.status === "rejected" ? "red" : "orange"
                          }`,
                        }}
                      />
                    </TableCell>
                    <TableCell>{new Date(k.created_at).toLocaleDateString()}</TableCell>
                    <TableCell sx={{ display: "flex", gap: 1 }}>
                      <Button
                        variant="outlined"
                        onClick={() => setPreviewDoc(k.document_front)}
                        sx={{ color: "#0ff", borderColor: "#0ff" }}
                      >
                        Preview
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        disabled={isLoading}
                        onClick={() => handleAction(k.id, "approve")}
                      >
                        {isLoading ? <CircularProgress size={18} color="inherit" /> : "Approve"}
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        disabled={isLoading}
                        onClick={() => handleAction(k.id, "reject")}
                      >
                        {isLoading ? <CircularProgress size={18} color="inherit" /> : "Reject"}
                      </Button>
                    </TableCell>
                  </motion.tr>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Preview Dialog */}
      {previewDoc && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setPreviewDoc(null)}
        >
          <Box
            component="img"
            src={previewDoc}
            sx={{ maxWidth: "90%", maxHeight: "90%", borderRadius: 2 }}
          />
        </Box>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}